//#region imports

import React, { useEffect, useState } from 'react';

// libraries
import { Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

// components
import PassengerRow from './passenger-row';

// styles
import { Colors } from '_styles';

// task colors
import * as TaskColors from '../../timeline/utils/colors';

// redux slices
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import {
    arrivalPaxCheckIn,
    paxCheckOut,
    resetPaxCheckOutFlags,
    resetUnRejectPaxFlags,
    resetArrivalPaxCheckInFlags,
    selectArrivalPaxCheckInStatus,
    selectPaxCheckOutStatus,
    selectUnRejectPaxStatus,
    setBookingLocation,
    selectSetBookingLocationStatus,
    resetSetBookingLocationFlags,
} from '_redux/slices/timeline/timeline-data';
import { setFlightModal } from '_redux/slices/timeline/timeline-control';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import { selectRejectReasonsByType } from '_redux/slices/timeline/reject-reasons';
import { selectFirebaseInfo, selectFirebaseUser } from "_redux/slices/main-screen/user";
import { selectUpdateStatus, resetUpdateBooking } from '_redux/slices/timeline/timeline-bookings';
import { selectSelectedTasksList, selectSelectedTask, setSelectedTaskList, setSelectedTask } from '_redux/slices/timeline/selected-task';


//#endregion

/**
 * ViewFlightPaxList Component
 *
 * Displays a list of passengers for a selected flight with summary statistics and individual passenger rows.
 * Manages passenger check-in/check-out operations and delegates individual row rendering to PassengerRow components.
 */
const ViewFlightPaxList = ({ selectedFlightToView, passengerFlightList, flightType, onRefresh }) => {

    //console.log('selectedFlightToView', selectedFlightToView);
    //console.log('passengerFlightList', passengerFlightList);


    //#region redux

    const dispatch = useDispatch();

    // location and theme selectors
    const usedTheme = useSelector(selectUsedTheme);
    const arrivalPaxCheckInStatus = useSelector(selectArrivalPaxCheckInStatus);
    const unRejectPaxStatus = useSelector(selectUnRejectPaxStatus);
    const paxCheckOutStatus = useSelector(selectPaxCheckOutStatus);
    const updateBookingStatus = useSelector(selectUpdateStatus);
    const dbUser = useSelector(selectFirebaseInfo);                                                 // logged in user
    const rejectReasons = useSelector((state) => selectRejectReasonsByType(state, "ALL"))
    const firebaseUser = useSelector(selectFirebaseUser);
    const selectedTasksList = useSelector(selectSelectedTasksList);
    const selectedTask = useSelector(selectSelectedTask);
    const setBookingLocationStatus = useSelector(selectSetBookingLocationStatus)

    //#endregion

    //#region local states

    // flight type
    const [isDeparture, setIsDeparture] = useState(true);
    const [passengerToCheckOut, setPassengerToCheckOut] = useState(null);
    const [passengerToUnReject, setPassengerToUnReject] = useState(null);


    // Set header background color based on flight type (same logic as card-bottom.js)
    const getHeaderBackgroundColor = (flightType) => {
        if (flightType?.toLowerCase() === 'boarding') {
            return TaskColors.combinedBooking; // '#e895a4' - pinkish color for boarding
        } else if (flightType?.toLowerCase() === 'departure') {
            return Colors.PRM_LIGHT_BLUE; // blue for departures
        } /*else if (flightType?.toLowerCase() === 'alt-pickup') {
            return 'purple'; // purple for alt pickups
        }*/ else {
            return Colors.ARRIVALS_GREEN; // '#29C40D' - green for arrivals
        }
    };

    const headerBackgroundColor = getHeaderBackgroundColor(flightType);
    const isJersey = dbUser?.details?.authority === "JER";

    const handleCheckOut = (pax) => {
        setPassengerToCheckOut(pax)
        if (!pax) return;
        if (isDeparture) {
            const paxLocation = {
                bookingId: pax._id || pax.id,
                activity: "Check-out",
                location: "Check-out",
                locationTitle: "Check-out",
                time: new Date(),
                checkInFlag: false,
                setBy: {
                    id: firebaseUser?.uid ?? "",
                    name: firebaseUser?.displayName ?? "",
                    from: "portal",
                    time: new Date(),
                }
            }
            dispatch(setBookingLocation(paxLocation))
        }
        else {
            dispatch(arrivalPaxCheckIn({
                bookingId: pax?.originalBookingId ? pax?.originalBookingId : pax?._id,
                body: {
                    type: "checkOut",
                    iata: dbUser.details.authority,
                    setBy: {
                        id: firebaseUser?.uid ?? "",
                        name: firebaseUser?.displayName ?? "",
                        from: "portal",
                        time: new Date(),
                    }
                }
            }));

        }
    };


    const selectedTasksListIds = (selectedTasksList || []).map(t => t?._id);

    const handleAssignPax = (pax) => {
        const paxId = pax?.originalBookingId || pax?._id;
        if (!paxId) return;

        const alreadySelected = selectedTasksListIds.includes(paxId) || selectedTask?._id === paxId;
        if (alreadySelected) return;

        if (!selectedTask) {
            dispatch(setSelectedTask(pax));
        } else {
            dispatch(setSelectedTaskList([...selectedTasksList, pax]));
        }

        dispatch(setFlightModal(false));
        dispatch(setToast({ message: 'Booking added to assignment list', statusType: 'success' }));
        dispatch(setShowToast(true));
    };

    //#endregion

    //#region useEffect hooks

    useEffect(() => {
        setPassengerToCheckOut(null);
    }, []);

    // Refresh list when check-out succeeds (departures)
    useEffect(() => {
        if (paxCheckOutStatus == "succeeded") {
            dispatch(resetPaxCheckOutFlags());
            setPassengerToCheckOut(null);
            // Refresh passenger list after successful check-out
            if (onRefresh) onRefresh();
        }
    }, [paxCheckOutStatus, onRefresh]);

    // Refresh list when check-out succeeds (departures)
    useEffect(() => {
        if (setBookingLocationStatus == "succeeded") {
            dispatch(resetSetBookingLocationFlags());
            setPassengerToCheckOut(null);
            // Refresh passenger list after successful check-out
            if (onRefresh) onRefresh();
        }
    }, [setBookingLocationStatus, onRefresh]);

    // Refresh list when un-reject succeeds
    useEffect(() => {
        if (unRejectPaxStatus == "succeeded") {
            dispatch(resetUnRejectPaxFlags());
            // Refresh passenger list after successful un-reject
            if (onRefresh) onRefresh();
        }
    }, [unRejectPaxStatus, onRefresh]);

    // Refresh list when arrival check-in/check-out succeeds
    useEffect(() => {
        if (arrivalPaxCheckInStatus === "succeeded") {
            setPassengerToCheckOut(null);
            dispatch(resetArrivalPaxCheckInFlags());
            // Refresh passenger list after successful check-in/check-out
            if (onRefresh) onRefresh();
        } else if (arrivalPaxCheckInStatus === "failed") {
            setPassengerToCheckOut(null);
            dispatch(resetArrivalPaxCheckInFlags());
        }
    }, [arrivalPaxCheckInStatus, onRefresh]);

    // Refresh list when booking edit succeeds
    useEffect(() => {
        if (updateBookingStatus === "succeeded") {
            dispatch(resetUpdateBooking());
            // Refresh passenger list after successful edit
            if (onRefresh) onRefresh();
        }
    }, [updateBookingStatus, onRefresh]);

    // determine flight type based on selected flight
    useEffect(() => {
        if (selectedFlightToView?.type === "departure" || selectedFlightToView?.type === "boarding") {
            setIsDeparture(true);
        } else {
            setIsDeparture(false);
        }
    }, [selectedFlightToView]);
    //#endregion


    useEffect(() => {
        //console.log("Passenger Flight List Updated: ", passengerFlightList);
    }, [passengerFlightList]);

    //#region render

    return (
        <div>

            {/* Passenger List */}
            <div style={{
                border: `1px solid ${usedTheme?.BORDER_COLOR || '#e5e7eb'}`,
                borderRadius: '8px',
                overflow: 'hidden',
            }}>
                {/* Table Header */}
                <div style={{
                    backgroundColor: headerBackgroundColor,
                    borderBottom: `1px solid ${usedTheme?.BORDER_COLOR || '#e5e7eb'}`,
                    padding: '12px 16px'
                }}>

                    <Row>
                        <Col style={{ flex: '0 0 12.5%', maxWidth: '12.5%' }}>
                            <strong style={{ color: 'white', fontSize: '13px' }}>Pax Name</strong>
                        </Col>
                        <Col md={isJersey ? 2 : 1}>
                            <strong style={{ color: 'white', fontSize: '13px' }}>SSR</strong>
                        </Col>
                        <Col md={isJersey ? 3 : 2}>
                            <strong style={{ color: 'white', fontSize: '13px' }}>
                                {isDeparture ? "Preboard" : "Ramp"}
                            </strong>
                        </Col>
                        <Col md={isJersey ? 3 : 2}>
                            <strong style={{ color: 'white', fontSize: '13px' }}>
                                {isDeparture ? "Employee" : "Employee"}
                            </strong>
                        </Col>
                        {!isJersey && (
                            <Col md={2}>
                                <strong style={{ color: 'white', fontSize: '13px' }}>
                                    {isDeparture ? "Boarding" : "Arrival"}
                                </strong>
                            </Col>
                        )}
                        {!isJersey && (
                            <Col md={2}>
                                <strong style={{ color: 'white', fontSize: '13px' }}>
                                    {"Employee"}
                                </strong>
                            </Col>
                        )}
                        <Col md={1}>
                            <strong style={{ color: 'white', fontSize: '13px' }}>Actions</strong>
                        </Col>
                    </Row>
                </div>

                {/* Table Body */}
                <div>
                    {(
                        passengerFlightList.length > 0 && passengerFlightList.map((passenger, idx) => (
                            <div
                                key={idx}
                                style={{ borderBottom: idx < passengerFlightList.length - 1 ? `1px solid ${usedTheme?.BORDER_COLOR || '#e5e7eb'}` : 'none' }}
                            >
                                <PassengerRow
                                    passenger={passenger}
                                    usedTheme={usedTheme}
                                    dispatch={dispatch}
                                    selectedFlightToView={selectedFlightToView}
                                    isDeparture={isDeparture}
                                    iata={dbUser.details.authority}
                                    isJersey={isJersey}
                                    passengerToCheckOut={passengerToCheckOut}
                                    arrivalPaxCheckInStatus={arrivalPaxCheckInStatus}
                                    paxCheckOutStatus={paxCheckOutStatus}
                                    handleCheckOut={handleCheckOut}
                                    unRejectPaxStatus={unRejectPaxStatus}
                                    passengerToUnReject={passengerToUnReject}
                                    setPassengerToUnReject={setPassengerToUnReject}
                                    rejectReasons={rejectReasons}
                                    onAssign={handleAssignPax}
                                    isInAssignList={
                                        selectedTasksListIds.includes(passenger?.originalBookingId || passenger?._id)
                                        || selectedTask?._id === (passenger?.originalBookingId || passenger?._id)
                                    }
                                />
                            </div>
                        ))
                    )}
                </div>
            </div>

        </div>
    );

    //#endregion
}

export default ViewFlightPaxList
{/* Passenger Detail Modal */ }
